import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, MailWarning, RefreshCw, Send, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("bbc_admin_token")}` } });

const fetchOverview = () => axios.get(`${API}/admin/email/bounces`, authHeader()).then((r) => r.data);

const when = (iso) => {
  if (!iso) return "never";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
};

export const EmailBounceBanner = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchOverview()
      .then(setData)
      .catch(() => setData(null));
  }, []);

  if (!data || !data.unresolved_hard) return null;

  const clientHard = data.bounces.filter((b) => b.kind === "hard" && !b.resolved && b.scope !== "internal");

  return (
    <div
      className="border border-red-800/70 bg-red-950/40 px-5 py-4 mb-6 flex items-start gap-3"
      data-testid="bounce-banner"
    >
      <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
      <div>
        <p className="text-red-200 text-sm font-medium">
          {data.unresolved_hard} email address{data.unresolved_hard === 1 ? "" : "es"} hard-bounced
        </p>
        <p className="text-red-300/70 text-[13px] mt-1">
          Mail to {clientHard
            .slice(0, 3)
            .map((b) => b.address)
            .join(", ")}
          {data.unresolved_hard > 3 ? " and others" : ""} is being rejected permanently. Open the Marketing tab →
          Email health to see why.
        </p>
      </div>
    </div>
  );
};

export const AdminEmailHealthCard = () => {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [checks, setChecks] = useState({});
  const [checking, setChecking] = useState(null);

  const load = useCallback(() => {
    fetchOverview()
      .then(setData)
      .catch(() => setData(null));
  }, []);

  useEffect(load, [load]);

  const scan = async () => {
    setScanning(true);
    try {
      const { data: result } = await axios.post(`${API}/admin/email/bounces/scan`, {}, authHeader());
      if (result.error) toast.error(result.error);
      else
        toast.success(
          `Read ${result.scanned} notification${result.scanned === 1 ? "" : "s"} — ${result.recorded} bounce${
            result.recorded === 1 ? "" : "s"
          } recorded`
        );
      load();
    } catch {
      toast.error("Bounce scan failed");
    } finally {
      setScanning(false);
    }
  };

  const clear = async (address) => {
    try {
      await axios.post(`${API}/admin/email/bounces/clear`, { address }, authHeader());
      toast.success(`${address} marked as handled`);
      load();
    } catch {
      toast.error("Could not clear that bounce");
    }
  };

  const verify = async (key, address) => {
    setChecking(key);
    try {
      const { data: result } = await axios.post(
        `${API}/admin/email/verify-recipient`,
        { address },
        authHeader()
      );
      setChecks((prev) => ({ ...prev, [key]: result }));
      if (result.deliverable) toast.success(`${address} accepts mail`);
      else toast.error(`${address}: ${result.message}`, { duration: 9000 });
    } catch {
      toast.error("Could not reach that mail server");
    } finally {
      setChecking(null);
    }
  };

  const sendTest = async () => {
    if (!testTo.includes("@")) return toast.error("Enter a valid address");
    setSending(true);
    try {
      const { data: result } = await axios.post(`${API}/admin/email/test`, { to: testTo }, authHeader());
      if (!result.success) toast.error(result.error || "Send failed");
      else if (result.self_send) toast.success(result.note, { duration: 9000 });
      else toast.success(`Sent via ${result.configured_transport}`);
      load();
    } catch {
      toast.error("Could not send the test email");
    } finally {
      setSending(false);
    }
  };

  if (!data) return null;
  const unresolved = data.bounces.filter((b) => !b.resolved && b.scope !== "internal");
  const internal = data.bounces.filter((b) => !b.resolved && b.scope === "internal");

  const dismissInternal = async () => {
    try {
      const { data: result } = await axios.post(
        `${API}/admin/email/bounces/clear-scope?scope=internal`,
        {},
        authHeader()
      );
      toast.success(`Dismissed ${result.cleared} internal address${result.cleared === 1 ? "" : "es"}`);
      load();
    } catch {
      toast.error("Could not dismiss those");
    }
  };

  return (
    <div className="bg-slate-900/50 border border-slate-800 p-6" data-testid="email-health-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
        <h2 className="font-serif text-xl text-white flex items-center gap-2">
          <MailWarning className="w-5 h-5 text-[#D4AF37]" />
          Email health
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={scan}
          disabled={scanning}
          data-testid="bounce-scan-btn"
          className="border-slate-700 text-slate-300 hover:text-white rounded-none"
        >
          {scanning ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-2" />}
          Check for bounces
        </Button>
      </div>
      <p className="text-slate-400 text-sm mb-6 max-w-2xl">
        Addresses Google rejects outright are recorded the moment we try to send. Delayed bounce notices are read
        from {data.mailbox || "the clinic mailbox"} every 30 minutes, read-only — nothing in the inbox is marked or
        moved.
      </p>

      <div className="grid sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Client hard bounces", value: data.unresolved_hard, testid: "bounce-hard-count" },
          { label: "Client soft bounces", value: data.unresolved_soft, testid: "bounce-soft-count" },
          { label: "Internal / test", value: data.unresolved_internal, testid: "bounce-internal-count" },
          { label: "Mailbox scan", value: data.scan_enabled ? "On" : "Off", testid: "bounce-scan-state" },
        ].map((item) => (
          <div key={item.label} className="border border-slate-800 bg-slate-950/60 p-4" data-testid={item.testid}>
            <p className="font-serif text-2xl text-[#D4AF37]">{item.value}</p>
            <p className="text-slate-500 text-[10px] uppercase tracking-widest mt-1">{item.label}</p>
          </div>
        ))}
      </div>

      {data.last_scan?.error && (
        <p className="border border-amber-800/60 bg-amber-950/20 text-amber-200 text-xs px-4 py-3 mb-5" data-testid="bounce-scan-error">
          {data.last_scan.error}
        </p>
      )}

      {unresolved.length === 0 ? (
        <p className="text-slate-400 text-sm inline-flex items-center gap-2" data-testid="bounce-empty">
          <ShieldCheck size={14} className="text-emerald-400" />
          No client address is bouncing. Last mailbox check: {when(data.last_scan_at)}.
        </p>
      ) : (
        <div className="space-y-2" data-testid="bounce-list">
          {unresolved.map((bounce, index) => (
            <div
              key={bounce.address}
              className={`border px-4 py-3 flex flex-wrap items-start justify-between gap-3 ${
                bounce.kind === "hard" ? "border-red-900/60 bg-red-950/20" : "border-amber-900/50 bg-amber-950/10"
              }`}
              data-testid={`bounce-row-${index}`}
            >
              <div className="min-w-0">
                <p className="text-white text-sm break-all">
                  {bounce.address}
                  <span
                    className={`ml-3 text-[10px] uppercase tracking-widest ${
                      bounce.kind === "hard" ? "text-red-400" : "text-amber-400"
                    }`}
                  >
                    {bounce.kind} {bounce.status}
                  </span>
                </p>
                <p className="text-slate-400 text-xs mt-1 break-words">{bounce.reason}</p>
                <p className="text-slate-600 text-[11px] mt-1">
                  {bounce.count} failure{bounce.count === 1 ? "" : "s"} · last {when(bounce.last_seen)}
                  {bounce.last_subject ? ` · "${bounce.last_subject}"` : ""}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => clear(bounce.address)}
                data-testid={`bounce-clear-${index}`}
                className="text-slate-400 hover:text-white text-xs"
              >
                Mark handled
              </Button>
            </div>
          ))}
        </div>
      )}

      {internal.length > 0 && (
        <div className="mt-6 border border-slate-800 bg-slate-950/40 p-4" data-testid="bounce-internal-block">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-slate-400 text-xs">
              {internal.length} address{internal.length === 1 ? "" : "es"} on badbossclinic.com also bounced — these
              come from the test suite, not from clients.
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={dismissInternal}
              data-testid="bounce-dismiss-internal"
              className="text-slate-400 hover:text-white text-xs"
            >
              Dismiss all
            </Button>
          </div>
          <p className="text-slate-600 text-[11px] mt-2 break-all">
            {internal.slice(0, 6).map((b) => b.address).join(", ")}
            {internal.length > 6 ? ` +${internal.length - 6} more` : ""}
          </p>
        </div>
      )}

      <div className="mt-8 pt-6 border-t border-slate-800" data-testid="reply-addresses">
        <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-1">Where replies land</p>
        <p className="text-slate-500 text-[11px] mb-4">
          Mail always leaves as {data.mailbox} — Google only lets us send as the signed-in mailbox. These are the
          addresses a reply goes to.
        </p>
        <div className="space-y-3">
          {[
            {
              key: "client",
              address: data.reply_to_client || data.reply_to_internal || data.mailbox,
              title: "Client replies",
              detail: "Confirmations, reminders, recaps, review requests, cancellations, reports",
              pending: !data.reply_to_client,
            },
            {
              key: "internal",
              address: data.reply_to_internal || data.mailbox,
              title: "Internal replies",
              detail: "Admin booking alerts, weekly digest, prep briefs, cancellation notices",
              pending: false,
            },
          ].map((row) => {
            const check = checks[row.key];
            return (
              <div
                key={row.key}
                className="border border-slate-800 bg-slate-950/60 p-4 flex flex-wrap items-start justify-between gap-3"
                data-testid={`reply-address-${row.key}`}
              >
                <div className="min-w-0">
                  <p className="text-white text-sm break-all">
                    {row.address}
                    {row.pending && (
                      <span className="ml-3 text-[10px] uppercase tracking-widest text-amber-400">
                        no separate mailbox yet
                      </span>
                    )}
                  </p>
                  <p className="text-slate-500 text-xs mt-1">
                    {row.title} — {row.detail}
                  </p>
                  {check && (
                    <p
                      className={`text-xs mt-2 inline-flex items-start gap-2 ${
                        check.deliverable ? "text-emerald-400" : "text-red-400"
                      }`}
                      data-testid={`verify-result-${row.key}`}
                    >
                      {check.deliverable ? <ShieldCheck size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
                      <span>
                        {check.deliverable ? "Accepted by " : "Rejected by "}
                        {check.mx || "the mail server"}
                        {check.code ? ` (${check.code})` : ""} — {check.message}
                      </span>
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => verify(row.key, row.address)}
                  disabled={checking === row.key}
                  data-testid={`verify-${row.key}-btn`}
                  className="border-slate-700 text-slate-300 hover:text-white rounded-none text-xs"
                >
                  {checking === row.key ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}
                  Verify delivers
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-slate-800">
        <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-3">Send a test email</p>
        <div className="flex flex-wrap gap-3">
          <Input
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            data-testid="email-test-input"
            className="bg-slate-950 border-slate-800 text-white rounded-none max-w-xs"
          />
          <Button
            onClick={sendTest}
            disabled={sending}
            data-testid="email-test-btn"
            className="bg-[#D4AF37] text-slate-950 hover:bg-[#c5a333] rounded-none"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-2" />}
            Send test
          </Button>
        </div>
        <p className="text-slate-600 text-[11px] mt-3">
          Use an address outside badbossclinic.com — Gmail never files a self-addressed message in the Inbox.
        </p>
      </div>
    </div>
  );
};
